import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { PageHead, when } from "@/studio/ui";
import { StyleLibrary } from "@/studio/StyleLibrary";
import { CompanySettings, PaymentProvider, ServicePricing, TermsSettings } from "@/studio/SettingsB3";

const LABELS = { primary_bedroom: "Primary bedroom", secondary_bedroom: "Secondary bedroom", living_room: "Living room", dining_room: "Dining room", living_dining_flex: "Living / dining flex", patio: "Patio", other: "Other" };
const FIXED = ["primary_bedroom", "secondary_bedroom", "living_room", "dining_room", "living_dining_flex"];
const BUDGETS = ["secondary_bedroom", "primary_bedroom", "living_room", "dining_room", "living_dining_flex", "patio"];

const Num = ({ value, onChange, testId, money = true, placeholder }) => (
  <span className="st-money">
    {money && <span>$</span>}
    <input className="st-input" type="number" min="0" value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} style={money ? null : { paddingLeft: 12 }} />
  </span>
);

const toNum = (v) => (v === "" || v == null ? null : Number(v));

export default function Settings() {
  const [s, setS] = useState(null);
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminApi.get("/studio/settings").then((r) => setS(r.data));
    adminApi.get("/studio/settings/versions").then((r) => setVersions(r.data));
  };
  useEffect(() => { load(); }, []);
  if (!s) return <p className="st-loading">Loading…</p>;

  const set = (section, key, field, v) => setS((x) => ({ ...x, [section]: { ...x[section], [key]: field ? { ...x[section][key], [field]: v } : v } }));

  const save = async () => {
    const fees = Object.fromEntries(Object.entries(s.design_fees).map(([k, v]) => [k, Object.fromEntries(Object.entries(v).map(([f, n]) => [f, toNum(n)]))]));
    const sizes = Object.fromEntries(Object.entries(s.max_sizes).map(([k, v]) => [k, toNum(v)]));
    const budgets = Object.fromEntries(Object.entries(s.furniture_budgets).map(([k, v]) => [k, { min: toNum(v.min), max: toNum(v.max) }]));
    setBusy(true);
    try {
      const { data } = await adminApi.put("/studio/settings", { design_fees: fees, max_sizes: sizes, furniture_budgets: budgets });
      toast.success(`Saved as settings version ${data.version}. Existing projects keep their prices.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Check the values and try again"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="st-page" data-testid="studio-settings-page">
      <PageHead eyebrow="Settings" title="Studio settings" lead="Saving creates a new settings version. Existing projects keep the prices they were created with.">
        <span className="st-tag st-tag--sage" style={{ alignSelf: "center" }} data-testid="settings-current-version">Current v{s.version}</span>
        <button className="st-btn" onClick={save} disabled={busy} data-testid="settings-save-button">{busy ? "Saving…" : "Save new version"}</button>
      </PageHead>
      <div className="st-grid2">
        <div>
          <section className="st-panel" data-testid="settings-design-fees">
            <h2 className="st-h2">Design fees</h2>
            <table className="st-settings-table"><tbody>
              {FIXED.map((k) => (
                <tr key={k}><td>{LABELS[k]}</td><td><Num value={s.design_fees[k].amount} onChange={(v) => set("design_fees", k, "amount", v)} testId={`settings-fee-${k}`} /></td></tr>
              ))}
              <tr><td>Patio <span className="st-muted st-small">· range, staff enters final</span></td>
                <td style={{ display: "flex", gap: 8, alignItems: "center" }}><Num value={s.design_fees.patio.min} onChange={(v) => set("design_fees", "patio", "min", v)} testId="settings-fee-patio-min" /> – <Num value={s.design_fees.patio.max} onChange={(v) => set("design_fees", "patio", "max", v)} testId="settings-fee-patio-max" /></td></tr>
              <tr><td>Other <span className="st-muted st-small">· starting at, staff enters final</span></td>
                <td><Num value={s.design_fees.other.min} onChange={(v) => set("design_fees", "other", "min", v)} testId="settings-fee-other-min" /></td></tr>
            </tbody></table>
          </section>
          <section className="st-panel" data-testid="settings-furniture-budgets">
            <h2 className="st-h2">Recommended furniture budgets</h2>
            <p className="st-muted st-small" style={{ marginTop: -8 }}>Used for each room's recommended range (snapshotted on the project). Separate from design fees.</p>
            <table className="st-settings-table"><tbody>
              {BUDGETS.map((k) => (
                <tr key={k}><td>{LABELS[k]}</td>
                  <td style={{ display: "flex", gap: 8, alignItems: "center" }}><Num value={s.furniture_budgets[k].min} onChange={(v) => set("furniture_budgets", k, "min", v)} testId={`settings-budget-${k}-min`} /> – <Num value={s.furniture_budgets[k].max} onChange={(v) => set("furniture_budgets", k, "max", v)} testId={`settings-budget-${k}-max`} /></td></tr>
              ))}
            </tbody></table>
          </section>
        </div>
        <div>
          <section className="st-panel" data-testid="settings-max-sizes">
            <h2 className="st-h2">Max room sizes</h2>
            <p className="st-muted st-small" style={{ marginTop: -8 }}>Larger rooms are flagged “Custom pricing required”.</p>
            <table className="st-settings-table"><tbody>
              {FIXED.map((k) => (
                <tr key={k}><td>{LABELS[k]}</td><td><Num money={false} value={s.max_sizes[k]} onChange={(v) => set("max_sizes", k, null, v)} testId={`settings-max-${k}`} placeholder="sq ft" /></td><td className="st-muted st-small">sq ft</td></tr>
              ))}
            </tbody></table>
          </section>
          <section className="st-panel" data-testid="settings-versions">
            <h2 className="st-h2">Version history</h2>
            <ul className="st-list st-activity">
              {versions.map((v) => <li key={v.version}><span>Version {v.version} <span className="st-muted">· {v.created_by}</span></span><time>{when(v.created_at)}</time></li>)}
            </ul>
          </section>
          <CompanySettings />
          <PaymentProvider />
          <ServicePricing />
        </div>
      </div>
      <div style={{ marginTop: 24 }}><TermsSettings /></div>
      <div style={{ marginTop: 24 }}><StyleLibrary /></div>
    </div>
  );
}
